/**
 * Клієнтська копія resolveMapping з backend (apps/api/src/modules/automations/config.ts).
 * Тримати в синхроні! UI використовує цю функцію для live-preview значень
 * у ExpressionInput-полях.
 *
 * Підтримує:
 *   - {{ <JS expr> }} — обчислюється з $, $json, $now
 *   - "$.path" — JSONPath-light (короткий шлях без curly braces)
 *   - решта — literal-string
 *
 * Версія для UI повертає не лише value, а й тип результату (ok/error) — щоб
 * preview-плашка могла показати або зелений ✓, або червоний ✗ з помилкою.
 */

export interface ExprResult {
  status: 'ok' | 'literal' | 'error' | 'empty';
  value: unknown;
  error?: string;
}

export function evaluateMapping(expr: string, payload: unknown): ExprResult {
  if (expr === null || expr === undefined || expr === '') {
    return { status: 'empty', value: undefined };
  }
  if (typeof expr !== 'string') {
    return { status: 'literal', value: expr };
  }
  const s = expr.trim();
  if (s === '') return { status: 'empty', value: undefined };

  // {{ expression }} — JS вираз
  const m = s.match(/^\{\{\s*([\s\S]+?)\s*\}\}$/);
  if (m) {
    const inner = m[1]!.trim();
    if (/^\$(?:\.[a-zA-Z0-9_]+|\[\d+\])*$/.test(inner)) {
      return { status: 'ok', value: jsonPathLight(inner, payload) };
    }
    try {
      const fn = new Function('$', '$json', '$now', `"use strict"; return (${inner});`);
      const v = fn(payload, payload, new Date());
      return { status: 'ok', value: v };
    } catch (e) {
      return { status: 'error', value: undefined, error: (e as Error).message };
    }
  }

  // Чистий $.path або $
  if (s === '$' || s.startsWith('$.') || s.startsWith('$[')) {
    return { status: 'ok', value: jsonPathLight(s, payload) };
  }

  return { status: 'literal', value: expr };
}

function jsonPathLight(path: string, payload: unknown): unknown {
  if (path === '$') return payload;
  let cur: unknown = payload;
  const rest = path.slice(1);
  const regex = /\.([a-zA-Z0-9_]+)|\[(\d+)\]/g;
  let mm: RegExpExecArray | null;
  while ((mm = regex.exec(rest)) !== null) {
    if (cur === null || cur === undefined) return undefined;
    const key = mm[1] ?? mm[2];
    if (key === undefined) return undefined;
    if (typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

/** Форматування результату у короткий preview-string. */
export function formatExprResult(v: unknown): string {
  if (v === undefined) return '—';
  if (v === null) return 'null';
  if (typeof v === 'string') return v.length > 80 ? `"${v.slice(0, 80)}…"` : `"${v}"`;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) return v.toISOString();
  try {
    const s = JSON.stringify(v);
    return s.length > 80 ? s.slice(0, 80) + '…' : s;
  } catch {
    return String(v);
  }
}
